import React, { useState, useCallback } from 'react';

const panelStyle = {
  width: 280,
  background: '#1a1a2e',
  borderLeft: '1px solid rgba(102,126,234,0.15)',
  padding: 16,
  overflowY: 'auto',
  color: '#eee',
  fontFamily: 'sans-serif',
  borderRadius: '8px 0 0 8px',
};

const btnStyle = {
  padding: '10px 16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  width: '100%',
  marginTop: 10,
  transition: 'opacity 0.2s',
};

const inputStyle = {
  width: 60,
  padding: '6px 8px',
  background: '#0f0f1a',
  color: '#eee',
  border: '1px solid #333',
  borderRadius: 6,
  fontSize: 12,
  textAlign: 'center',
  outline: 'none',
};

/**
 * PenToolPanel — single component managing pen tool anchors and clip action.
 * Users add points manually via x,y coordinate inputs.
 */
export function PenToolPanel({ editor }) {
  const [anchors, setAnchors] = useState([]);
  const [newX, setNewX] = useState('');
  const [newY, setNewY] = useState('');

  const handleAddPoint = useCallback(() => {
    const x = Number(newX);
    const y = Number(newY);
    if (isNaN(x) || isNaN(y)) return;
    setAnchors((prev) => [
      ...prev,
      { x, y, handle_in_x: null, handle_in_y: null, handle_out_x: null, handle_out_y: null },
    ]);
    setNewX('');
    setNewY('');
  }, [newX, newY]);

  const handleRemovePoint = useCallback((idx) => {
    setAnchors((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleClip = useCallback(() => {
    if (anchors.length < 3) return;
    editor.penToolClip({ path: anchors });
  }, [anchors, editor]);

  const handleClear = useCallback(() => {
    setAnchors([]);
  }, []);

  return (
    <div style={panelStyle}>
      <h3 style={{ margin: '0 0 8px', fontSize: 14, color: '#ddd' }}>✒️ Pen Tool</h3>

      <div style={{
        padding: '10px 12px', background: '#0f0f1a', borderRadius: 6,
        marginBottom: 12, border: '1px solid #333',
      }}>
        <p style={{ fontSize: 11, color: '#888', margin: 0 }}>
          Add anchor points by entering coordinates below. Minimum 3 points required to clip.
        </p>
      </div>

      <div style={{ fontSize: 12, color: '#ccc', marginBottom: 8 }}>
        Points: <span style={{ color: '#667eea', fontWeight: 600 }}>{anchors.length}</span>
        {anchors.length < 3 && <span style={{ color: '#666', marginLeft: 4 }}>(min 3)</span>}
      </div>

      {/* Point list */}
      {anchors.length > 0 && (
        <div style={{
          maxHeight: 120, overflowY: 'auto', marginBottom: 10,
          background: '#0f0f1a', borderRadius: 6, padding: 6,
        }}>
          {anchors.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '3px 6px', fontSize: 11, color: '#aaa',
            }}>
              <span>#{i + 1}: ({a.x}, {a.y})</span>
              <button
                onClick={() => handleRemovePoint(i)}
                style={{
                  background: 'none', border: 'none', color: '#e74c3c',
                  cursor: 'pointer', fontSize: 12, padding: '0 4px',
                }}
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Add point inputs */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#888' }}>X</span>
        <input
          type="number" value={newX} onChange={(e) => setNewX(e.target.value)}
          placeholder="0" style={inputStyle}
          onKeyDown={(e) => e.key === 'Enter' && handleAddPoint()}
        />
        <span style={{ fontSize: 11, color: '#888' }}>Y</span>
        <input
          type="number" value={newY} onChange={(e) => setNewY(e.target.value)}
          placeholder="0" style={inputStyle}
          onKeyDown={(e) => e.key === 'Enter' && handleAddPoint()}
        />
      </div>
      <button
        onClick={handleAddPoint}
        style={{
          ...btnStyle, background: '#0f0f1a', border: '1px solid #333',
          color: '#ccc', fontWeight: 400, fontSize: 12, marginTop: 4,
        }}
      >
        + Add Point
      </button>

      <div style={{ height: 1, background: '#333', margin: '14px 0' }} />

      <button
        style={{
          ...btnStyle,
          opacity: anchors.length < 3 ? 0.4 : 1,
          cursor: anchors.length < 3 ? 'not-allowed' : 'pointer',
          marginTop: 0,
        }}
        onClick={handleClip}
        disabled={anchors.length < 3}
      >
        ✂️ Clip Path
      </button>
      <button
        onClick={handleClear}
        style={{
          ...btnStyle, background: 'transparent', border: '1px solid #333',
          color: '#999', fontWeight: 400, fontSize: 12,
        }}
      >
        Clear Points
      </button>
    </div>
  );
}

export default PenToolPanel;
